import { BasicFacetTermV2, FilterRange, ProductRequestType } from '@ravago/shared/page-data/models/elastic';

import { createParser } from 'nuqs/server';

import { CHILDREN_TERMS_DELIMITER, PARENT_KEY_DELIMITER, RANGE_DELIMITER, RANGE_MIN_MAX_DELIMITER } from './filter-query-utils';

// ---------------------------------------------------------------------------
// Individual term serialisation helpers (mirrors filter-query-utils logic)
// ---------------------------------------------------------------------------

const serializeTerm = (term: BasicFacetTermV2): string => {
   const parentPart = term.parentKey ? `${PARENT_KEY_DELIMITER}${term.parentKey}` : '';
   const rangePart = term.range ? `${RANGE_DELIMITER}${term.range.min}${RANGE_MIN_MAX_DELIMITER}${term.range.max}` : '';
   return `${term.key}${parentPart}${rangePart}`;
};

const serializeTerms = (terms: BasicFacetTermV2[]): string => terms.map(serializeTerm).join(CHILDREN_TERMS_DELIMITER);

const compareTerms = (a: BasicFacetTermV2[], b: BasicFacetTermV2[]): boolean => {
   if (a.length !== b.length) return false;
   const sortedA = [...a].map(serializeTerm).sort((a, b) => a.localeCompare(b));
   const sortedB = [...b].map(serializeTerm).sort((a, b) => a.localeCompare(b));
   return sortedA.every((term, i) => term === sortedB[i]);
};

// ---------------------------------------------------------------------------
// parseAsDefaultFacetTerms
// Handles simple comma-separated keys, e.g. "ABS,PA6,POM"
// ---------------------------------------------------------------------------

export const parseAsDefaultFacetTerms = createParser<BasicFacetTermV2[]>({
   parse(value: string): BasicFacetTermV2[] | null {
      if (!value) return null;
      const terms = value
         .split(CHILDREN_TERMS_DELIMITER)
         .map((raw) => raw.trim())
         .filter(Boolean)
         .map((raw): BasicFacetTermV2 => ({ key: raw }));
      return terms.length > 0 ? terms : null;
   },
   serialize: serializeTerms,
   eq: compareTerms
});

// ---------------------------------------------------------------------------
// parseAsNestedFacetTerms
// Handles keys that may carry a parent key, e.g. "Child---Parent,Standalone"
// ---------------------------------------------------------------------------

export const parseAsNestedFacetTerms = createParser<BasicFacetTermV2[]>({
   parse(value: string): BasicFacetTermV2[] | null {
      if (!value) return null;
      const terms = value
         .split(CHILDREN_TERMS_DELIMITER)
         .map((raw) => raw.trim())
         .filter(Boolean)
         .map((raw): BasicFacetTermV2 => {
            const [child, parent] = raw.split(PARENT_KEY_DELIMITER);
            return parent ? { key: child, parentKey: parent } : { key: child };
         });
      return terms.length > 0 ? terms : null;
   },
   serialize: serializeTerms,
   eq: compareTerms
});

// ---------------------------------------------------------------------------
// parseAsCountryFacetTerms
// Like parseAsDefaultFacetTerms but supports an explicit "none" sentinel so
// callers can distinguish "no country param present (→ use default)" from
// "user explicitly deselected all countries (→ empty selection)".
//
//   URL param absent  → null  → nuqs falls back to .withDefault() value
//   URL param = "none" → []   → empty selection, overrides any default
//   URL param = "BE,NL" → [{ key: 'BE' }, { key: 'NL' }]
// ---------------------------------------------------------------------------

export const COUNTRY_NONE_SENTINEL = 'none';

export const parseAsCountryFacetTerms = createParser<BasicFacetTermV2[]>({
   parse(value: string): BasicFacetTermV2[] | null {
      if (!value) return null;
      if (value === COUNTRY_NONE_SENTINEL) return [];
      const terms = value
         .split(CHILDREN_TERMS_DELIMITER)
         .map((raw) => raw.trim())
         .filter(Boolean)
         .map((raw): BasicFacetTermV2 => ({ key: raw }));
      return terms.length > 0 ? terms : null;
   },
   serialize(terms: BasicFacetTermV2[]): string {
      if (terms.length === 0) return COUNTRY_NONE_SENTINEL;
      return serializeTerms(terms);
   },
   eq: compareTerms
});

// ---------------------------------------------------------------------------
// parseAsRangeFacetTerms
// Handles keys with an optional numeric range, e.g. "Glass__10_30,Carbon"
// ---------------------------------------------------------------------------

const parseRange = (raw: string): { key: string; range?: FilterRange } => {
   const splitTerm = raw.split(RANGE_DELIMITER);
   if (splitTerm.length < 2) return { key: raw };

   const [min, max] = splitTerm[1].split(RANGE_MIN_MAX_DELIMITER).map(Number);
   if (Number.isNaN(min) || Number.isNaN(max)) return { key: splitTerm[0] };

   return { key: splitTerm[0], range: { min, max } };
};

export const parseAsRangeFacetTerms = createParser<BasicFacetTermV2[]>({
   parse(value: string): BasicFacetTermV2[] | null {
      if (!value) return null;
      const terms = value
         .split(CHILDREN_TERMS_DELIMITER)
         .map((raw) => raw.trim())
         .filter(Boolean)
         .map(parseRange);
      return terms.length > 0 ? terms : null;
   },
   serialize: serializeTerms,
   eq: compareTerms
});

// ---------------------------------------------------------------------------
// parseAsTab
// Handles product portfolio tabs
// ---------------------------------------------------------------------------

export const parseAsTab = createParser<ProductRequestType>({
   parse(value) {
      if (value === 'products') return 'product';
      if (value === 'brands') return 'brand';
      if (value === 'case-study') return 'caseStudy';
      return null;
   },
   serialize(value) {
      if (value === 'product') return 'products';
      if (value === 'brand') return 'brands';
      if (value === 'caseStudy') return 'case-study';
      return '';
   }
});
